import { z } from 'zod';

/**
 * Document vault — a per-company filing cabinet.
 *
 * Deliberately separate from `attachment.schemas`: an attachment must belong to
 * a record, whereas a vault document need not. You drop a stack of PDFs and
 * they are filed by DATE, which is the axis you actually search on later.
 *
 * (Owned by the vault work — extend freely.)
 */

const ISO_DATE = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'must be YYYY-MM-DD');

/** 25 MB — same ceiling as attachments; these are scans, not archives. */
export const MAX_VAULT_DOCUMENT_BYTES = 25 * 1024 * 1024;

export const vaultDocTypes = [
  'bank_statement',
  'invoice',
  'receipt',
  'bill',
  'tax',
  'payroll',
  'contract',
  'insurance',
  'legal',
  /** A reconciliation packet generated by the system, not uploaded by hand. */
  'reconciliation',
  /** A month-end regulatory packet generated by the system. */
  'eom_package',
  'other',
] as const;
export type VaultDocType = (typeof vaultDocTypes)[number];
export const vaultDocTypeSchema = z.enum(vaultDocTypes);

export const vaultExtractionStatuses = ['pending', 'processing', 'done', 'failed'] as const;
export type VaultExtractionStatus = (typeof vaultExtractionStatuses)[number];
export const vaultExtractionStatusSchema = z.enum(vaultExtractionStatuses);

export const uploadVaultDocumentInputSchema = z.object({
  fileName: z.string().min(1).max(300),
  mimeType: z.string().min(1).max(150),
  /** base64-encoded file contents. */
  content: z.string().min(1),
});
export type UploadVaultDocumentInput = z.infer<typeof uploadVaultDocumentInputSchema>;

/** Correct what the AI read off a document, or file one it could not read. */
export const updateVaultDocumentInputSchema = z.object({
  documentDate: ISO_DATE.nullable().optional(),
  docType: vaultDocTypeSchema.nullable().optional(),
  vendorName: z.string().max(200).nullable().optional(),
  summary: z.string().max(500).nullable().optional(),
  amount: z
    .string()
    .regex(/^-?\d+(\.\d{1,4})?$/, 'must be a decimal string')
    .nullable()
    .optional(),
  reviewedByUser: z.boolean().optional(),
});
export type UpdateVaultDocumentInput = z.infer<typeof updateVaultDocumentInputSchema>;

/**
 * The folder that completed reconciliation packets are filed into.
 *
 * A single well-known name rather than a free-for-all: the packet filename is
 * fixed too, so a year of reconciliations sorts correctly on its own.
 */
export const RECONCILIATION_FOLDER = 'Reconciliations';

/**
 * Filename for a completed reconciliation packet.
 *
 * The date is the STATEMENT ENDING date, never the date the reconciliation was
 * completed — you find last May's packet by looking for May, not by remembering
 * which day in July you got round to reconciling it.
 */
export function reconciliationPacketFileName(statementDate: string): string {
  return `${statementDate.replaceAll('-', '')} - Reconciliation Package.pdf`;
}

export const vaultDocumentSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  fileName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number(),
  sha256: z.string(),
  documentDate: z.string().nullable(),
  docType: z.string().nullable(),
  vendorName: z.string().nullable(),
  amount: z.string().nullable(),
  summary: z.string().nullable(),
  extractionStatus: vaultExtractionStatusSchema,
  extractionError: z.string().nullable(),
  extractionConfidence: z.string().nullable(),
  reviewedByUser: z.boolean(),
  /** Named folder, or null when the document is filed by date alone. */
  folder: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type VaultDocument = z.infer<typeof vaultDocumentSchema>;

export const listVaultDocumentsQuerySchema = z.object({
  /** Full-text search across file name, vendor, summary and extracted text. */
  q: z.string().max(200).optional(),
  docType: vaultDocTypeSchema.optional(),
  from: ISO_DATE.optional(),
  to: ISO_DATE.optional(),
  /** Only documents the AI could not confidently file. */
  needsReview: z.coerce.boolean().optional(),
  /**
   * Which folder to browse. Omitted = the loose, date-filed documents only, so
   * generated reconciliation packets never bury the paperwork you dropped in.
   * Pass a name to open that folder, or `*` for everything regardless of folder.
   */
  folder: z.string().max(100).optional(),
  limit: z.coerce.number().int().min(1).max(500).default(200),
  offset: z.coerce.number().int().min(0).default(0),
});
export type ListVaultDocumentsQuery = z.infer<typeof listVaultDocumentsQuerySchema>;

/** One Year → Month → documents node, for the browse tree. */
export interface VaultDateGroup {
  year: string;
  months: {
    month: string; // "01".."12"
    label: string; // "January"
    documents: VaultDocument[];
  }[];
}
